/**
 * @file PlanSettingRow.tsx
 * @brief One row of a plan card on Super Admin → Plans & seats: label left,
 *        value right with its unit; click to edit in place
 *
 * @details
 * DES 22-10-2026 (W8): one value per row, no setting keys on screen (the key
 * is in the label's hover tooltip), a dot on changed rows. Editing writes the
 * same form value the side sheet does; nothing is saved until the changes
 * bar. Enter or leaving the field closes the editor, Escape puts back what
 * the row showed when editing started. Invalid values follow the bare-field
 * rule: the error replaces the label in red.
 *
 * Related: SubscriptionPlansCard.tsx, PlanEditSheet.tsx, ../launchPlans.ts
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useRef } from "react";
import { Switch } from "@/components/ui/Switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";
import { editAffixes, rowLabel, rowValue, type PlanSetting } from "../launchPlans";
import { fieldError, keyTip, type PlanForm } from "../planForm";

export function ChangedDot({ on, className }: { on: boolean; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        on ? "bg-brand" : "bg-transparent",
        className,
      )}
    />
  );
}

function RowLabel({ s, form, label }: { s: PlanSetting; form: PlanForm; label?: string }) {
  const bad = form.invalid.has(s.key);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "min-w-0 flex-1 text-sm leading-snug",
            bad ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {bad ? fieldError(s) : (label ?? rowLabel(s))}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[320px] break-all font-mono text-[11px]">
        {keyTip(s, form)}
      </TooltipContent>
    </Tooltip>
  );
}

/** A switch row: the switch on the right, a plain line under the label. */
export function PlanSwitchRow({
  s,
  form,
  hint,
}: {
  s: PlanSetting;
  form: PlanForm;
  hint?: string;
}) {
  const changed = form.value(s.key) !== (form.baseline[s.key] ?? "");
  return (
    <div className="flex items-start gap-2 py-2">
      <span className="mt-2">
        <ChangedDot on={changed} />
      </span>
      <div className="min-w-0 flex-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="text-sm font-medium text-foreground">{s.label}</p>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[320px] break-all font-mono text-[11px]">
            {keyTip(s, form)}
          </TooltipContent>
        </Tooltip>
        {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <Switch
        checked={form.value(s.key) === "true"}
        onCheckedChange={(on) => form.set(s.key, on ? "true" : "false")}
        aria-label={s.label}
      />
    </div>
  );
}

export function PlanSettingRow({
  s,
  form,
  editing,
  onEdit,
  onClose,
  label,
  valueText,
  sub,
}: {
  s: PlanSetting;
  form: PlanForm;
  editing: boolean;
  onEdit: () => void;
  onClose: () => void;
  /** The compare row's label (W18), e.g. "Meeting Notes"; default rowLabel(s). */
  label?: string;
  /** The compare row's value text (W18); default rowValue(s, current). */
  valueText?: string;
  /** W34: a small line under the label (Free's one pool). */
  sub?: string;
}) {
  const current = form.value(s.key);
  const changed = current !== (form.baseline[s.key] ?? "");
  // What the row showed when editing started, for Escape.
  const start = useRef(current);

  if (editing) {
    const [prefix, suffix] = editAffixes(s);
    const unlimited = s.kind === "limit" && current.trim() === "-1";
    const before = form.baseline[s.key] ?? "";
    return (
      <div className="flex items-center gap-2 py-1.5">
        <ChangedDot on={changed} />
        <RowLabel s={s} form={form} label={label} />
        <div className="flex shrink-0 items-center gap-1.5 text-sm">
          {prefix ? <span className="text-muted-foreground">{prefix}</span> : null}
          <input
            autoFocus
            aria-label={label ?? rowLabel(s)}
            inputMode="decimal"
            value={unlimited ? "Unlimited" : current}
            // Read-only, not disabled: a disabled field drops the focus and
            // the editor would close as Unlimited is switched on.
            readOnly={unlimited}
            onChange={(e) => form.set(s.key, e.target.value)}
            onFocus={(e) => {
              start.current = current;
              e.currentTarget.select();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onClose();
              if (e.key === "Escape") {
                e.stopPropagation();
                form.set(s.key, start.current);
                onClose();
              }
            }}
            onBlur={(e) => {
              // Moving to the Unlimited switch keeps the editor open.
              if (e.relatedTarget?.closest("[data-plan-row-editor]")) return;
              onClose();
            }}
            className="w-24 border-b border-foreground/30 bg-transparent py-0.5 text-right font-medium tabular-nums text-foreground outline-none focus:border-brand read-only:text-muted-foreground"
          />
          {s.kind === "limit" ? (
            <label
              data-plan-row-editor=""
              // Keep the focus in the number field, so pressing the switch
              // doesn't close the editor first.
              onPointerDown={(e) => e.preventDefault()}
              className="ml-1 flex items-center gap-1.5 text-xs text-muted-foreground"
            >
              <Switch
                checked={unlimited}
                onCheckedChange={(on) =>
                  form.set(s.key, on ? "-1" : before !== "-1" ? before : "")
                }
                onBlur={(e) => {
                  if (e.relatedTarget?.closest("[data-plan-row-editor], input")) return;
                  onClose();
                }}
                aria-label={`${rowLabel(s)}: Unlimited`}
              />
              Unlimited
            </label>
          ) : suffix ? (
            <span className="text-muted-foreground">{suffix}</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onEdit}
      className={cn(
        "-mx-2 flex w-[calc(100%+1rem)] items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-black/[0.04] focus-visible:bg-black/[0.04] focus-visible:outline-none dark:hover:bg-white/[0.06] dark:focus-visible:bg-white/[0.06]",
        // The sub-line takes its own line under the row (W34).
        sub && "flex-wrap gap-y-0.5",
      )}
    >
      <ChangedDot on={changed} className="mt-[7px]" />
      <RowLabel s={s} form={form} label={label} />
      <span className="max-w-[58%] shrink-0 text-right text-sm font-medium tabular-nums text-foreground">
        {valueText ?? rowValue(s, current)}
      </span>
      {/* W34: the full width under the row, lined up with the label. */}
      {sub ? <span className="w-full pl-3.5 text-xs leading-snug text-muted-foreground/80">{sub}</span> : null}
    </button>
  );
}

/**
 * A value the page shows but doesn't edit here; the tooltip says where.
 * `dim` (W34): a price this plan never uses, greyed.
 */
export function ReadOnlyRow({ label, value, tip, dim }: { label: string; value: string; tip: string; dim?: boolean }) {
  return (
    <div className={cn("flex items-start gap-2 py-1.5", dim && "opacity-60")}>
      <ChangedDot on={false} className="mt-[7px]" />
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="min-w-0 flex-1 text-sm leading-snug text-muted-foreground">{label}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          {tip}
        </TooltipContent>
      </Tooltip>
      <span className="max-w-[58%] shrink-0 text-right text-sm font-medium tabular-nums text-muted-foreground">
        {value}
      </span>
    </div>
  );
}
